import type { AthenaIngressIR } from "./ir.ts";
import type {
	EventIngressDatabase,
	EventIngressPersistence,
	EventIngressRecord,
} from "./persistence.ts";

function asRecord(value: unknown): Record<string, string> {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, string>;
	}
	return {};
}

type EventIngressRow = {
	id: string;
	domain: string;
	provider: string | null;
	provider_event_id: string | null;
	connection_id: string | null;
	status: EventIngressRecord["status"];
	body: Buffer;
	headers: unknown;
	received_at: Date;
	correlation_id: string | null;
	trace_id: string | null;
	attempt_count: number;
};

function mapIngressRow(row: EventIngressRow): EventIngressRecord {
	return {
		id: row.id,
		domain: row.domain,
		provider: row.provider,
		providerEventId: row.provider_event_id,
		connectionId: row.connection_id,
		status: row.status,
		body: new Uint8Array(row.body),
		headers: asRecord(row.headers),
		receivedAt: new Date(row.received_at),
		correlationId: row.correlation_id,
		traceId: row.trace_id,
		attemptCount: row.attempt_count,
	};
}

function insertValues(
	ingress: AthenaIngressIR,
	meta: { provider: string | null; providerEventId: string | null },
): unknown[] {
	return [
		ingress.id,
		ingress.domain,
		meta.provider,
		meta.providerEventId,
		ingress.connectionId ?? null,
		ingress.receivedAt.toISOString(),
		Buffer.from(ingress.body),
		JSON.stringify(ingress.headers),
		ingress.correlationId ?? null,
		ingress.traceId ?? null,
	];
}

async function lockProviderEventClaim(
	tx: EventIngressDatabase,
	input: {
		domain: string;
		provider: string | null;
		providerEventId: string;
	},
): Promise<void> {
	await tx.query(
		`SELECT pg_advisory_xact_lock(hashtext($1), hashtext($2))`,
		[
			input.domain,
			`${input.provider ?? ""}\u0000${input.providerEventId}`,
		],
	);
}

async function selectByProviderEvent(
	tx: EventIngressDatabase,
	input: {
		domain: string;
		provider: string | null;
		providerEventId: string;
	},
): Promise<{ id: string } | undefined> {
	const result = await tx.query<{ id: string }>(
		`
SELECT id, status
FROM athena.event_ingress
WHERE domain = $1
  AND provider IS NOT DISTINCT FROM $2
  AND provider_event_id = $3
`,
		[input.domain, input.provider, input.providerEventId],
	);
	return result.rows[0];
}

function duplicateFromRow(
	row: { id: string },
	ingressId: string,
): { duplicate: boolean; ingressId: string } {
	if (row.id !== ingressId) {
		return {
			duplicate: true,
			ingressId: row.id,
		};
	}
	return {
		duplicate: false,
		ingressId: row.id,
	};
}

async function insertReceivedById(
	tx: EventIngressDatabase,
	ingress: AthenaIngressIR,
	provider: string | null,
): Promise<void> {
	await tx.query(
		`
INSERT INTO athena.event_ingress (
  id, domain, provider, provider_event_id, connection_id,
  received_at, body, headers, status, correlation_id, trace_id, attempt_count
) VALUES (
  $1, $2, $3, $4, $5, $6, $7, $8::jsonb, 'received', $9, $10, 0
)
ON CONFLICT (id) DO NOTHING
`,
		insertValues(ingress, { provider, providerEventId: null }),
	);
}

async function claimProviderEventOnRow(
	tx: EventIngressDatabase,
	input: {
		ingressId: string;
		domain: string;
		provider: string | null;
		providerEventId: string;
	},
): Promise<string | undefined> {
	const result = await tx.query<{ id: string }>(
		`
UPDATE athena.event_ingress AS target
SET provider_event_id = $4
WHERE target.id = $1
  AND (target.provider_event_id IS NULL OR target.provider_event_id = $4)
  AND NOT EXISTS (
    SELECT 1
    FROM athena.event_ingress AS claimed
    WHERE claimed.domain = $2
      AND claimed.provider IS NOT DISTINCT FROM $3
      AND claimed.provider_event_id = $4
      AND claimed.id <> $1
  )
RETURNING target.id
`,
		[input.ingressId, input.domain, input.provider, input.providerEventId],
	);
	return result.rows[0]?.id;
}

async function insertReceivedByProviderEvent(
	tx: EventIngressDatabase,
	ingress: AthenaIngressIR,
	meta: { provider: string | null; providerEventId: string },
): Promise<string | undefined> {
	const result = await tx.query<{ id: string }>(
		`
INSERT INTO athena.event_ingress (
  id, domain, provider, provider_event_id, connection_id,
  received_at, body, headers, status, correlation_id, trace_id, attempt_count
) VALUES (
  $1, $2, $3, $4, $5, $6, $7, $8::jsonb, 'received', $9, $10, 0
)
ON CONFLICT (domain, provider, provider_event_id) WHERE provider_event_id IS NOT NULL
DO NOTHING
RETURNING id
`,
		insertValues(ingress, meta),
	);
	return result.rows[0]?.id;
}

export function createSqlEventIngressPersistence(): EventIngressPersistence {
	return {
		async insertReceived(tx, ingress, meta) {
			const provider = meta.provider ?? null;
			const providerEventId = meta.providerEventId;
			if (!providerEventId) {
				await insertReceivedById(tx, ingress, provider);
				return {
					duplicate: false,
					ingressId: ingress.id,
				};
			}
			await lockProviderEventClaim(tx, {
				domain: ingress.domain,
				provider,
				providerEventId,
			});
			const existing = await selectByProviderEvent(tx, {
				domain: ingress.domain,
				provider,
				providerEventId,
			});
			if (existing) {
				return duplicateFromRow(existing, ingress.id);
			}
			const claimedOnRow = await claimProviderEventOnRow(tx, {
				ingressId: ingress.id,
				domain: ingress.domain,
				provider,
				providerEventId,
			});
			if (claimedOnRow) {
				return {
					duplicate: false,
					ingressId: claimedOnRow,
				};
			}
			const afterClaim = await selectByProviderEvent(tx, {
				domain: ingress.domain,
				provider,
				providerEventId,
			});
			if (afterClaim) {
				return duplicateFromRow(afterClaim, ingress.id);
			}
			const inserted = await insertReceivedByProviderEvent(tx, ingress, {
				provider,
				providerEventId,
			});
			if (inserted) {
				return {
					duplicate: false,
					ingressId: inserted,
				};
			}
			const afterInsert = await selectByProviderEvent(tx, {
				domain: ingress.domain,
				provider,
				providerEventId,
			});
			if (afterInsert) {
				return duplicateFromRow(afterInsert, ingress.id);
			}
			return {
				duplicate: false,
				ingressId: ingress.id,
			};
		},
		async lock(tx, ingressId) {
			const result = await tx.query<EventIngressRow>(
				`SELECT * FROM athena.event_ingress WHERE id = $1 FOR UPDATE`,
				[ingressId],
			);
			const row = result.rows[0];
			if (!row) {
				return undefined;
			}
			return mapIngressRow(row);
		},
		async markStatus(tx, input) {
			await tx.query(
				`
UPDATE athena.event_ingress
SET status = $2,
    attempt_count = attempt_count + 1,
    failed_at = CASE WHEN $2 IN ('retryable_failure', 'terminal_failure') THEN now() ELSE failed_at END,
    error = $3::jsonb
WHERE id = $1
`,
				[
					input.ingressId,
					input.status,
					input.error ? JSON.stringify({ message: String(input.error) }) : null,
				],
			);
		},
		async markProcessed(tx, input) {
			await tx.query(
				`
UPDATE athena.event_ingress
SET status = 'processed', processed_at = now()
WHERE id = $1
`,
				[input.ingressId],
			);
			void input.eventIds;
		},
	};
}

export function toDirectIngress(ingress: AthenaIngressIR): AthenaIngressIR {
	return { ...ingress, transport: { kind: "direct" } };
}
